import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { publicCmsAPI } from '../../lib/cmsApi';
import {
  MapPin,
  ArrowRight,
  Droplets,
  Building2,
  Route,
  Loader2,
} from 'lucide-react';

const ProjectsPage = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, partnersRes] = await Promise.all([
          publicCmsAPI.getProjects(),
          publicCmsAPI.getPartners(),
        ]);
        setProjects(projectsRes.data || []);
        setPartners(partnersRes.data || []);
      } catch (error) {
        console.error('Error fetching projects data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  const categories = [...new Set(projects.map(p => p.category))];

  return (
    <div data-testid="projects-page">
      {/* Hero Section */}
      <section className="bg-[#1D1D1B] py-20 lg:py-24">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="text-[#FFD537] font-semibold mb-4 animate-fadeIn">NOS REALISATIONS</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6 animate-fadeIn">
              Nos projets
            </h1>
            <p className="text-xl text-white/70 leading-relaxed animate-fadeIn animation-delay-100">
              Notre expertise concretisee a travers nos realisations au Quebec et en Amerique du Nord.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Banner */}
      {categories.length > 0 && (
        <section
          className="py-8"
          style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
        >
          <div className="container-app">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {categories.map((cat, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Droplets className="w-6 h-6 text-[#1D1D1B]" />
                  <span className="text-[#1D1D1B] font-medium">{cat}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Projects Grid */}
      <section className="section-padding bg-white">
        <div className="container-app">
          {projects.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-500 text-lg">Aucun projet publie pour le moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {projects.map((project, index) => (
                <Card
                  key={project.id}
                  className="overflow-hidden group animate-fadeIn"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`project-card-${project.slug}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image_url || 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=800&q=80'}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1B]/90 via-[#1D1D1B]/40 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <Badge
                        className="text-[#1D1D1B]"
                        style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                      >
                        {project.category}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <div className="flex items-center gap-2 text-sm text-[#FFD537] mb-2">
                        <MapPin className="w-4 h-4" />
                        {project.location}
                      </div>
                      <h3 className="font-heading font-semibold text-xl">{project.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-neutral-600 mb-4">{project.short_description}</p>
                    {project.client_name && (
                      <p className="text-sm text-neutral-500">
                        <span className="font-medium">Client:</span> {project.client_name}
                      </p>
                    )}
                    {project.year && (
                      <p className="text-sm text-neutral-400 mt-1">Annee: {project.year}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Partners Section */}
      {partners.length > 0 && (
        <section className="section-padding bg-neutral-50">
          <div className="container-app">
            <div className="text-center mb-12">
              <p className="text-[#F9B438] font-semibold mb-2">NOS PARTENAIRES</p>
              <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">
                Nos collaborateurs dans le paysage de l'ingenierie
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {partners.map((partner) => (
                <Card
                  key={partner.id}
                  className="p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                  >
                    <Building2 className="w-8 h-8 text-[#F9B438]" />
                  </div>
                  <p className="font-medium text-[#1D1D1B] text-sm">{partner.name}</p>
                  {partner.website_url && (
                    <a
                      href={partner.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#F9B438] hover:underline mt-1 inline-block"
                    >
                      Visiter le site
                    </a>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Expertise Areas */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-[#1D1D1B] mb-4">
              Domaines d'intervention
            </h2>
            <p className="text-neutral-600 max-w-2xl mx-auto">
              Nos projets couvrent plusieurs secteurs cles de l'ingenierie hydraulique et civile.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 text-center group">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
              >
                <Droplets className="w-8 h-8 text-[#F9B438]" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2 text-[#1D1D1B]">Milieux urbains</h3>
              <p className="text-neutral-600 text-sm">Gestion des eaux pluviales et drainage en environnement urbain dense.</p>
            </Card>
            <Card className="p-6 text-center group">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
              >
                <Route className="w-8 h-8 text-[#F9B438]" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2 text-[#1D1D1B]">Infrastructures routieres</h3>
              <p className="text-neutral-600 text-sm">Drainage et protection des routes, autoroutes et voies ferrees.</p>
            </Card>
            <Card className="p-6 text-center group">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
              >
                <Building2 className="w-8 h-8 text-[#F9B438]" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2 text-[#1D1D1B]">Secteur institutionnel</h3>
              <p className="text-neutral-600 text-sm">Projets pour les institutions publiques et municipalites.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1D1D1B] py-16">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Vous avez un projet similaire?
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contactez notre equipe pour discuter de vos besoins et decouvrir comment nous pouvons vous accompagner.
          </p>
          <Link to="/contact">
            <Button size="lg" data-testid="projects-cta-btn">
              Discuter de votre projet
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
